import dayjs from 'dayjs';

import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useState,
} from 'react';

import { toast } from 'react-toastify';

import {
  fetchMetadata,
  fetchProperties,
  fetchReportData,
} from '@api/analytics';

import { useAuth } from '@contexts/authContext';

export const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = memo(function AnalyticsProvider({ children }) {
  const { auth } = useAuth();
  const authToken = auth?.idToken;

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [timeSeriesReport, setTimeSeriesReport] = useState(null);
  const [pagesReport, setPagesReport] = useState(null);
  const [eventsReport, setEventsReport] = useState(null);
  const [countriesReport, setCountriesReport] = useState(null);
  const [browsersReport, setBrowsersReport] = useState(null);

  const [metadata, setMetadata] = useState(null);

  const [firstInit, setFirstInit] = useState(null);
  const [overviewInit, setOverviewInit] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [metadataError, setMetadataError] = useState(null);

  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [propertiesError, setPropertiesError] = useState(null);
  const [reportError, setReportError] = useState(null);

  const firstInitTransientRef = React.useRef(null);
  const overviewInitTransientRef = React.useRef(null);

  // Default date range
  const [startDate, setStartDate] = useState(
    dayjs().subtract(29, 'days').format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState(
    dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
  );

  // Function to change the date range
  const changeDateRange = (newStartDate, newEndDate) => {
    setStartDate(dayjs(newStartDate).format('YYYY-MM-DD'));
    setEndDate(dayjs(newEndDate).format('YYYY-MM-DD'));
  };

  useEffect(() => {
    return () => {
      firstInitTransientRef.current = null;
      overviewInitTransientRef.current = null;
    };
  }, []);

  // Effect to fetch the list of properties on component mount
  useEffect(() => {
    const loadProperties = async () => {
      if (firstInit === null || !authToken) return;

      if (firstInitTransientRef.current) {
        firstInitTransientRef.current = false;
        return;
      }

      try {
        setLoadingProperties(true);
        const fetchedProperties = await fetchProperties({ token: authToken });
        setProperties(fetchedProperties);

        // Set the first property as the default selected property
        // if (fetchedProperties && fetchedProperties.length > 0) {
        //   setSelectedProperty(fetchedProperties[0]);
        // }
      } catch (err) {
        console.error('Error fetching properties:', err);
        setPropertiesError(
          err.message || 'Failed to load Google Analytics properties.',
        );
        toast.error(`Failed to load properties: ${err.message}`);
      } finally {
        setLoadingProperties(false);
        if (firstInit) {
          setFirstInit(false);
          firstInitTransientRef.current = true;
        }
      }
    };

    loadProperties();
  }, [authToken, firstInit]);

  // Effect to fetch the list of metadata when the selected property
  useEffect(() => {
    const loadMetadata = async () => {
      if (firstInit || !selectedProperty?.propertyId || !authToken) return;

      try {
        setLoadingMetadata(true);
        const fetchedMetadata = await fetchMetadata({
          token: authToken,
          propertyId: selectedProperty.propertyId,
        });
        setMetadata(fetchedMetadata);
      } catch (err) {
        console.error('Error fetching metadata:', err);
        setMetadataError(
          err.message || 'Failed to load Google Analytics metadata.',
        );
        toast.error(`Failed to load metadata: ${err.message}`);
      } finally {
        setLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, [authToken, selectedProperty?.propertyId, firstInit]);

  // Effect to fetch all reports when the selected property or date range changes
  useEffect(() => {
    const loadAllReports = async () => {
      if (
        firstInit ||
        overviewInit === null ||
        !selectedProperty?.propertyId ||
        !authToken
      )
        return;

      if (overviewInitTransientRef.current) {
        overviewInitTransientRef.current = false;
        return;
      }

      try {
        setLoadingReport(true);
        setReportError(null);
        setTimeSeriesReport(null);
        setPagesReport(null);
        setEventsReport(null);
        setCountriesReport(null);
        setBrowsersReport(null);

        const filter = {
          propertyId: selectedProperty.propertyId,
          dateRanges: [{ startDate: startDate, endDate: endDate }],
        };

        // Fetch all the report data concurrently
        const [timeSeries, pages, events, countries, browsers] =
          await Promise.all([
            fetchReportData({
              token: authToken,
              filter: {
                ...filter,
                metrics: [
                  { name: 'screenPageViews' },
                  { name: 'activeUsers' },
                  { name: 'userEngagementDuration' },
                  { name: 'eventCount' },
                ],
                dimensions: [{ name: 'date' }],
              },
            }),
            fetchReportData({
              token: authToken,
              filter: {
                ...filter,
                metrics: [{ name: 'screenPageViews' }],
                dimensions: [
                  { name: 'unifiedPagePathScreen' },
                  { name: 'pageTitle' },
                ],
                orderBys: [
                  { metric: { metricName: 'screenPageViews' }, desc: true },
                ],
                limit: 10,
              },
            }),
            fetchReportData({
              token: authToken,
              filter: {
                ...filter,
                metrics: [{ name: 'eventCount' }],
                dimensions: [{ name: 'eventName' }],
                orderBys: [
                  { metric: { metricName: 'eventCount' }, desc: true },
                ],
                limit: 10,
              },
            }),
            fetchReportData({
              token: authToken,
              filter: {
                ...filter,
                metrics: [{ name: 'activeUsers' }],
                dimensions: [{ name: 'country' }],
                orderBys: [
                  { metric: { metricName: 'activeUsers' }, desc: true },
                ],
                limit: 10,
              },
            }),
            fetchReportData({
              token: authToken,
              filter: {
                ...filter,
                metrics: [{ name: 'activeUsers' }],
                dimensions: [{ name: 'browser' }],
                orderBys: [
                  { metric: { metricName: 'activeUsers' }, desc: true },
                ],
                limit: 10,
              },
            }),
          ]);

        // Update all the states
        setTimeSeriesReport(timeSeries.formattedReport || []);
        setPagesReport(pages.formattedReport || []);
        setEventsReport(events.formattedReport || []);
        setCountriesReport(countries.formattedReport || []);
        setBrowsersReport(browsers.formattedReport || []);

        // toast.success(
        //   `Reports for property ${selectedProperty.displayName} loaded!`,
        // );
      } catch (err) {
        console.error('Error fetching one or more GA reports:', err);
        setReportError(err.message || 'Failed to load some report data.');
        toast.error(`Failed to load report data: ${err.message}`);
      } finally {
        setLoadingReport(false);
        if (overviewInit) {
          setOverviewInit(false);
          overviewInitTransientRef.current = true;
        }
      }
    };

    loadAllReports();
  }, [selectedProperty, authToken, startDate, endDate, firstInit, overviewInit]);

  // Function to allow components to change the selected property
  const changeSelectedProperty = (property) => {
    setLoadingProperties(true);
    setSelectedProperty(property);
    setLoadingProperties(false);
  };

  const contextValue = React.useMemo(
    () => ({
      firstInit,
      setFirstInit,
      overviewInit,
      setOverviewInit,
      properties,
      selectedProperty,
      timeSeriesReport,
      pagesReport,
      eventsReport,
      countriesReport,
      browsersReport,
      metadata,
      loadingMetadata,
      metadataError,
      loadingProperties,
      loadingReport,
      propertiesError,
      reportError,
      changeSelectedProperty,
      authToken,
      startDate,
      endDate,
      changeDateRange,
    }),
    [
      firstInit,
      setFirstInit,
      overviewInit,
      setOverviewInit,
      properties,
      selectedProperty,
      timeSeriesReport,
      pagesReport,
      eventsReport,
      countriesReport,
      browsersReport,
      metadata,
      loadingMetadata,
      metadataError,
      loadingProperties,
      loadingReport,
      propertiesError,
      reportError,
      changeSelectedProperty,
      authToken,
      startDate,
      endDate,
      changeDateRange,
    ],
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
});
